import React, { useReducer, useState, useMemo } from "react";
import { 
    Container, Grid, TextField, Typography, MenuItem, 
    InputAdornment, Paper, Alert 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useHistory } from "react-router-dom";

// Componentes
import CustomBreadcrumbs from "../../../../components/breadcrumbs"; // Ajusta ruta
import ResponseComponent from "../../../../components/responseComponent" // "../../../../components/responseComponent"; // Ajusta ruta

// Controlador
import { createPlan } from "../../../../controllers/company/subscriptions/PlansController";

// Estado Inicial
const initialState = {
    name: '',           errorName: false,
    description: '',
    price: '',          errorPrice: false,
    
    // Lógica de Frecuencia
    interval_count: 1,  errorIntervalCount: false,
    billing_interval: 'month', // day, week, month, year
    
    trial_days: 0,

    isLoading: false,
    open: false, // Para el modal de respuesta
    title: '',
    message: '',
    success: false
};

function reducer(state, { field, value }) {
    return { ...state, [field]: value };
}

export default function PlanCreate() {
    const [state, setState] = useReducer(reducer, initialState);
    const history = useHistory();

    const {
        name, errorName, description,
        price, errorPrice,
        interval_count, errorIntervalCount, billing_interval,
        trial_days,
        isLoading, open, title, message, success
    } = state;

    const onChange = (e) => {
        setState({ field: e.target.name, value: e.target.value });
    };

    // --- GENERADOR DE TEXTO DE RESUMEN ---
    // Esto ayuda al usuario a entender la frecuencia que configuró
    const summaryText = useMemo(() => {
        if (!price) return "Define el precio y frecuencia.";
        
        const currency = "$"; // O tu moneda
        const map = { day: 'días', week: 'semanas', month: 'meses', year: 'años' };
        const singularMap = { day: 'día', week: 'semana', month: 'mes', year: 'año' };
        
        let frequency = "";
        if (parseInt(interval_count) === 1) {
            frequency = `cada ${singularMap[billing_interval]}`;
        } else {
            frequency = `cada ${interval_count} ${map[billing_interval]}`;
        }

        return `El cliente pagará ${currency}${parseFloat(price || 0).toFixed(2)} ${frequency}.`;
    }, [price, interval_count, billing_interval]);


    // --- VALIDACIÓN ---
    const formVerification = () => {
        let error = false;

        if (name === '') { setState({ field: 'errorName', value: true }); error = true; }
        if (price === '' || parseFloat(price) < 0) { setState({ field: 'errorPrice', value: true }); error = true; }
        if (interval_count === '' || parseInt(interval_count) < 1) { setState({ field: 'errorIntervalCount', value: true }); error = true; }

        return !error;
    };

    // --- ENVÍO ---
    const handleCreate = async () => {
        if (!formVerification()) return;

        setState({ field: 'isLoading', value: true });

        const data = {
            name,
            description,
            price,
            interval_count,
            billing_interval,
            trial_days
        };

        const response = await createPlan(data);

        if (response.success) {
            setState({ field: 'title', value: 'Plan Creado' });
            setState({ field: 'message', value: 'El plan de suscripción se ha creado exitosamente.' });
            setState({ field: 'success', value: true });
            setState({ field: 'open', value: true });
        } else {
            setState({ field: 'title', value: 'Error' });
            setState({ field: 'message', value: response.message });
            setState({ field: 'success', value: false });
            setState({ field: 'open', value: true });
        }

        setState({ field: 'isLoading', value: false });
    };

    const handleCloseResponse = () => {
        if (success) history.push('/subscriptions');
        else setState({ field: 'open', value: false });
    };

    return (
        <Container maxWidth="md">
            <CustomBreadcrumbs 
                returnTo='/subscriptions' 
                crumbs={[
                    { name: 'Suscripciones', link: '/subscriptions' },
                    { name: 'Planes', link: '/subscriptions/plans' },
                    { name: 'Crear', link: '#' }
                ]}
            />

            <Typography variant="h4" sx={{ mt: 4, mb: 4 }}>
                Crear Nuevo Plan
            </Typography>

            <Paper elevation={3} sx={{ p: 4 }}>
                <Grid container spacing={3}>
                    
                    {/* --- DATOS BÁSICOS --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>Información General</Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Nombre del Plan"
                            name="name"
                            placeholder="Ej. Karate Infantil - Mensual"
                            value={name}
                            onChange={onChange}
                            error={errorName}
                            helperText={errorName && "El nombre es requerido"}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Precio"
                            name="price"
                            type="number"
                            value={price}
                            onChange={onChange}
                            error={errorPrice}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Descripción (Opcional)"
                            name="description"
                            multiline
                            rows={2}
                            value={description}
                            onChange={onChange}
                            placeholder="Describe qué incluye este plan..."
                        />
                    </Grid>

                    {/* --- CONFIGURACIÓN DE COBRO --- */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Frecuencia de Cobro</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography>Cobrar cada:</Typography>
                            <TextField
                                sx={{ width: '80px' }}
                                name="interval_count"
                                type="number"
                                value={interval_count}
                                onChange={onChange}
                                error={errorIntervalCount}
                            />
                            <TextField
                                select
                                sx={{ flexGrow: 1 }}
                                name="billing_interval"
                                value={billing_interval}
                                onChange={onChange}
                            >
                                <MenuItem value="week">Semanas</MenuItem>
                                <MenuItem value="month">Meses</MenuItem>
                                <MenuItem value="year">Años</MenuItem>
                            </TextField>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Días de Prueba (Gratis)"
                            name="trial_days"
                            type="number"
                            value={trial_days}
                            onChange={onChange}
                            helperText="Días antes de realizar el primer cobro (0 = cobro inmediato)"
                        />
                    </Grid>

                    {/* --- RESUMEN VISUAL --- */}
                    <Grid item xs={12}>
                        <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                                Resumen:
                            </Typography>
                            {summaryText}
                        </Alert>
                    </Grid>

                    {/* --- BOTÓN --- */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <LoadingButton
                            variant="contained"
                            size="large"
                            fullWidth
                            loading={isLoading}
                            onClick={handleCreate}
                        >
                            Crear Plan
                        </LoadingButton>
                    </Grid>

                </Grid>
            </Paper>

            <ResponseComponent
                show={open}
                success={success}
                title={title}
                message={message}
                actionText="Aceptar"
                onClick={handleCloseResponse}
            />
        </Container>
    );
}